import {MinusOutlined, PlusOutlined} from '@ant-design/icons'
import {Button, Slider} from 'antd'
import React, {memo, useCallback} from 'react'
import {useMemo, useState} from 'react'

export type ZoomSliderProps = {
  step: number
  setStep: (value: number) => void
  steps: number
  min: number
  max: number
}

export function getZoomCoefficient({
  step,
  min,
  max,
  steps,
}: {
  step: number
  min: number
  max: number
  steps: number
}) {
  if (step === steps) {
    return 1
  }

  if (step > steps) {
    const numberOfStepsUp = step - steps
    const fractionOfStepsUp = numberOfStepsUp / steps
    const space = max - 1
    return 1 + space * fractionOfStepsUp
  }

  if (step < steps) {
    const numberOfStepsDown = steps - step
    const fractionOfStepsDown = numberOfStepsDown / steps
    const space = 1 - min
    return 1 - space * fractionOfStepsDown
  }
}

export function useZoomSliderSteps({
  min = 0.1,
  max = 3,
  steps = 10,
}: {
  min?: number
  max?: number
  steps?: number
} = {}) {
  const [step, setStep] = useState(steps)
  const zoomCoefficient = useMemo(
    () => getZoomCoefficient({step, steps, min, max}),
    [step, steps, min, max]
  )

  const setStepWithLimits = useCallback(
    (newValue: number) => {
      if (newValue < 0 || newValue > steps * 2) {
        return
      }

      setStep(Math.floor(newValue))
    },
    [steps, setStep]
  )

  return [step, setStepWithLimits, zoomCoefficient] as const
}

export function ZoomSlider({step, setStep, steps, min, max}: ZoomSliderProps) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 200,
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'all',
      }}
    >
      <Button
        size="small"
        shape="circle"
        icon={<PlusOutlined />}
        type="link"
        onClick={() => setStep(step + 1)}
      />
      <Slider
        vertical
        style={{flex: '1 1 auto'}}
        value={step}
        onChange={setStep}
        max={steps * 2}
        min={0}
        marks={{[steps]: {label: ' '}}}
        tooltip={{
          formatter: (value) => {
            const coefficient = getZoomCoefficient({step: value, min, max, steps})
            const rounded = Math.round(coefficient * 100) / 100
            return `x${rounded}`
          },
        }}
      />
      <Button
        size="small"
        shape="circle"
        icon={<MinusOutlined />}
        type="link"
        onClick={() => setStep(step - 1)}
      />
    </div>
  )
}

export const ZoomSliderMemoized = memo(ZoomSlider)
